import ExcelJS from "exceljs";
import { fmtDate } from "@/lib/format";

const DARK = "FF1E293B", AMBER = "FFD97706", CREAM = "FFFEF3C7", INPUT = "FFFFFBEB", GREY = "FFF1F5F9";
const NUM = "#,##0;[Red]-#,##0", NUM2 = "#,##0.00;[Red]-#,##0.00";
const fill = (c) => ({ type: "pattern", pattern: "solid", fgColor: { argb: c } });
const thin = { style: "thin", color: { argb: "FFE2E8F0" } };
const border = { top: thin, left: thin, bottom: thin, right: thin };

function styleRow(row, { bg, color = "FF0F172A", bold = false, size = 10 } = {}) {
  row.eachCell({ includeEmpty: true }, (c) => {
    if (bg) c.fill = fill(bg);
    c.font = { bold, color: { argb: color }, size, name: "Calibri" };
    c.border = border;
  });
}

function banner(ws, text, r, span, bg = AMBER) {
  ws.mergeCells(r, 1, r, span);
  const c = ws.getCell(r, 1);
  c.value = text;
  c.fill = fill(bg);
  c.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
}

const rangeLabel = (r) =>
  `${fmtDate(r.start, { day: "2-digit", month: "short", year: "numeric" })} to ${fmtDate(r.end, { day: "2-digit", month: "short", year: "numeric" })}`;

function shiftBlock(ws, r, row, title, key, dates, last) {
  banner(ws, title, row, last); row++;
  const hdr = ws.getRow(row);
  hdr.values = ["Category", "Item", "Rate (₹)", ...dates.map((d) => d.slice(8)), "Qty", "Sales (₹)"];
  styleRow(hdr, { bg: DARK, color: "FFFFFFFF", bold: true });
  const first = row + 1;
  const qc = 4 + dates.length, sc = qc + 1;
  r.items.forEach((it) => {
    row++;
    const x = ws.getRow(row);
    x.values = [it.category, it.name, it.rate, ...dates.map((d) => it[key][d] || null)];
    const a = ws.getCell(row, 4).address, b = ws.getCell(row, 3 + dates.length).address;
    x.getCell(qc).value = { formula: `SUM(${a}:${b})` };
    x.getCell(sc).value = { formula: `${ws.getCell(row, qc).address}*${ws.getCell(row, 3).address}` };
    styleRow(x);
    for (let c = 4; c < qc; c++) x.getCell(c).fill = fill(INPUT);
    x.getCell(sc).numFmt = NUM;
    x.getCell(3).numFmt = NUM;
  });
  const lastItem = row;
  row++;
  const tp = ws.getRow(row);
  tp.getCell(2).value = `${title} — PLATES`;
  for (let c = 4; c <= qc; c++) {
    const L = ws.getColumn(c).letter;
    tp.getCell(c).value = { formula: `SUM(${L}${first}:${L}${lastItem})` };
  }
  const SL = ws.getColumn(sc).letter;
  tp.getCell(sc).value = { formula: `SUM(${SL}${first}:${SL}${lastItem})` };
  tp.getCell(sc).numFmt = NUM;
  styleRow(tp, { bg: CREAM, bold: true, color: "FF78350F" });
  row++;
  const ds = ws.getRow(row);
  ds.getCell(2).value = `${title} — SALES (₹)`;
  dates.forEach((d, i) => {
    const L = ws.getColumn(4 + i).letter;
    ds.getCell(4 + i).value = { formula: `SUMPRODUCT($C${first}:$C${lastItem},${L}${first}:${L}${lastItem})` };
    ds.getCell(4 + i).numFmt = NUM;
  });
  ds.getCell(sc).value = { formula: `${SL}${row - 1}` };
  ds.getCell(sc).numFmt = NUM;
  styleRow(ds, { bg: CREAM, bold: true, color: "FF78350F" });
  return { next: row + 2, salesRow: row, platesRow: row - 1, sc, qc };
}

function trackerSheet(wb, r) {
  const ws = wb.addWorksheet("Tracker", { views: [{ state: "frozen", xSplit: 3, ySplit: 0 }] });
  const dates = r.days.map((d) => d.date);
  const last = 5 + dates.length;
  const s = r.summary, ded = s.deduction_pct / 100;
  ws.columns = [{ width: 14 }, { width: 38 }, { width: 10 }, ...dates.map(() => ({ width: 7 })), { width: 10 }, { width: 14 }];

  ws.mergeCells(1, 1, 1, last);
  ws.getCell(1, 1).value = `${r.business_name}  |  SALES & EXPENSES — ${rangeLabel(r)}`;
  ws.getCell(1, 1).font = { bold: true, size: 14, color: { argb: DARK } };
  ws.mergeCells(2, 1, 2, last);
  ws.getCell(2, 1).value = `Good Food. Great Mood.  —  Deduction ${s.deduction_pct}% on gross sales. Exported ${new Date().toLocaleString("en-IN")}`;
  ws.getCell(2, 1).font = { italic: true, size: 9, color: { argb: "FF64748B" } };

  const day = shiftBlock(ws, r, 4, "DAY SHIFT", "day_by_date", dates, last);
  const night = shiftBlock(ws, r, day.next, "NIGHT SHIFT", "night_by_date", dates, last);
  const sc = day.sc;
  const SL = ws.getColumn(sc).letter;

  let row = night.next;
  banner(ws, "DAY CLOSE — day + night combined", row, last, DARK); row++;
  const closeStart = row;
  const close = [
    ["TOTAL PLATES SERVED", (c) => `${ws.getCell(day.platesRow, c).address}+${ws.getCell(night.platesRow, c).address}`, null],
    ["TOTAL SALES (₹)", (c) => `${ws.getCell(day.salesRow, c).address}+${ws.getCell(night.salesRow, c).address}`, NUM],
    [`LESS: DEDUCTION @ ${s.deduction_pct}% (₹)`, (c) => `${ws.getCell(closeStart + 1, c).address}*${ded}`, NUM2],
    [`NET SALES — ${100 - s.deduction_pct}% (₹)`, (c) => `${ws.getCell(closeStart + 1, c).address}-${ws.getCell(closeStart + 2, c).address}`, NUM2],
  ];
  close.forEach(([label, f, fmt]) => {
    const x = ws.getRow(row);
    x.getCell(2).value = label;
    for (let c = 4; c < 4 + dates.length; c++) { x.getCell(c).value = { formula: f(c) }; if (fmt) x.getCell(c).numFmt = fmt; }
    const tc = label.startsWith("TOTAL PLATES") ? day.qc : sc;
    x.getCell(tc).value = { formula: f(tc) };
    if (fmt) x.getCell(tc).numFmt = fmt;
    styleRow(x, { bold: true });
    row++;
  });
  const grossCell = `${SL}${closeStart + 1}`, dedCell = `${SL}${closeStart + 2}`, netCell = `${SL}${closeStart + 3}`;

  row++;
  banner(ws, "DAILY / VARIABLE EXPENSES — amount spent (₹)", row, last); row++;
  const eh = ws.getRow(row);
  eh.values = ["Category", "Expense Head", "", ...dates.map((d) => d.slice(8)), "", "Total (₹)"];
  styleRow(eh, { bg: DARK, color: "FFFFFFFF", bold: true });
  const vars = r.expenses.filter((e) => e.kind === "variable");
  const ef = row + 1;
  vars.forEach((e) => {
    row++;
    const x = ws.getRow(row);
    x.values = [e.category, e.name, "₹", ...dates.map((d) => e.by_date[d] || null)];
    x.getCell(sc).value = { formula: `SUM(${ws.getCell(row, 4).address}:${ws.getCell(row, 3 + dates.length).address})` };
    x.getCell(sc).numFmt = NUM;
    styleRow(x);
    for (let c = 4; c < 4 + dates.length; c++) { x.getCell(c).fill = fill(INPUT); x.getCell(c).numFmt = NUM; }
  });
  row++;
  const vt = ws.getRow(row);
  vt.getCell(2).value = "TOTAL VARIABLE EXPENSE (₹)";
  for (let c = 4; c <= sc; c++) {
    if (c === day.qc) continue;
    const L = ws.getColumn(c).letter;
    vt.getCell(c).value = { formula: `SUM(${L}${ef}:${L}${row - 1})` };
    vt.getCell(c).numFmt = NUM;
  }
  styleRow(vt, { bg: CREAM, bold: true, color: "FF78350F" });
  const varCell = `${SL}${row}`;

  row += 2;
  banner(ws, "FIXED COSTS — pro-rated for the selected dates", row, last); row++;
  const ff = row;
  r.expenses.filter((e) => e.kind === "fixed").forEach((e) => {
    const x = ws.getRow(row);
    x.getCell(2).value = e.name;
    x.getCell(sc).value = Math.round(e.amount * 100) / 100;
    x.getCell(sc).numFmt = NUM2;
    x.getCell(sc).fill = fill(INPUT);
    styleRow(x);
    row++;
  });
  const ft = ws.getRow(row);
  ft.getCell(2).value = "TOTAL FIXED COSTS (₹)";
  ft.getCell(sc).value = { formula: `SUM(${SL}${ff}:${SL}${row - 1})` };
  ft.getCell(sc).numFmt = NUM2;
  styleRow(ft, { bg: CREAM, bold: true, color: "FF78350F" });
  const fixCell = `${SL}${row}`;

  row += 2;
  banner(ws, `P&L — ${rangeLabel(r)}`, row, last, DARK); row++;
  const pl = [
    ["Gross Sales (day + night)", grossCell, NUM2],
    [`Less: Deduction @ ${s.deduction_pct}%`, `-${dedCell}`, NUM2],
    [`Net Sales retained (${100 - s.deduction_pct}%)`, netCell, NUM2],
    ["Less: Variable / daily expenses", `-${varCell}`, NUM2],
    ["Less: Fixed costs", `-${fixCell}`, NUM2],
    ["Total Expenses", `${varCell}+${fixCell}`, NUM2],
    ["NET PROFIT / (LOSS)", `${netCell}-${varCell}-${fixCell}`, NUM2],
    ["Profit margin on gross sales", `IF(${grossCell}=0,0,(${netCell}-${varCell}-${fixCell})/${grossCell})`, "0.0%"],
  ];
  pl.forEach(([label, f, fmt], i) => {
    const x = ws.getRow(row);
    x.getCell(2).value = label;
    x.getCell(sc).value = { formula: f };
    x.getCell(sc).numFmt = fmt;
    styleRow(x, { bold: i === 6, bg: i === 6 ? CREAM : undefined });
    row++;
  });
  const td = ws.getRow(row);
  td.getCell(2).value = "Trading days";
  td.getCell(sc).value = s.trading_days;
  styleRow(td);
}

function tableSheet(wb, name, title, r, headers, rows, totals, widths, fmts = {}) {
  const ws = wb.addWorksheet(name, { views: [{ state: "frozen", ySplit: 3 }] });
  ws.columns = widths.map((w) => ({ width: w }));
  ws.mergeCells(1, 1, 1, headers.length);
  ws.getCell(1, 1).value = `${r.business_name}  |  ${title} — ${rangeLabel(r)}`;
  ws.getCell(1, 1).font = { bold: true, size: 13, color: { argb: DARK } };
  const h = ws.getRow(3);
  h.values = headers;
  styleRow(h, { bg: DARK, color: "FFFFFFFF", bold: true });
  rows.forEach((v, i) => {
    const x = ws.getRow(4 + i);
    x.values = v;
    styleRow(x, { bg: i % 2 ? GREY : undefined });
    Object.entries(fmts).forEach(([c, f]) => (x.getCell(Number(c)).numFmt = f));
  });
  if (totals) {
    const x = ws.getRow(4 + rows.length);
    x.values = totals;
    styleRow(x, { bg: CREAM, bold: true, color: "FF78350F" });
    Object.entries(fmts).forEach(([c, f]) => (x.getCell(Number(c)).numFmt = f));
  }
}

export async function exportReportExcel(r) {
  const wb = new ExcelJS.Workbook();
  wb.creator = "Manjap CRM";
  const s = r.summary;
  trackerSheet(wb, r);
  tableSheet(wb, "Day-wise Summary", "DAY-WISE SALES & EXPENSES", r,
    ["Date", "Day", "Plates", "Day Sales (₹)", "Night Sales (₹)", "Gross (₹)", `Deduction ${s.deduction_pct}% (₹)`, "Net Sales (₹)", "Variable Exp (₹)", "Net − Var (₹)"],
    r.days.map((d) => [fmtDate(d.date, { day: "2-digit", month: "short", year: "numeric" }), fmtDate(d.date, { weekday: "short" }), d.plates, d.day_sales, d.night_sales, d.gross, d.deduction, d.net, d.variable_expense, d.net_after_variable]),
    ["TOTAL", "", s.plates, s.day_sales, s.night_sales, s.gross, s.deduction, s.net, s.variable_expense, s.net - s.variable_expense],
    [14, 8, 10, 14, 14, 14, 16, 14, 16, 14], { 4: NUM, 5: NUM, 6: NUM, 7: NUM2, 8: NUM2, 9: NUM, 10: NUM2 });
  tableSheet(wb, "Weekly Summary", "WEEK-WISE SUMMARY", r,
    ["Week", "Trading Days", "Plates", "Gross (₹)", "Net Sales (₹)", "Variable Exp (₹)"],
    r.weeks.map((w) => [`${fmtDate(w.week_start)} – ${fmtDate(w.week_end)}`, w.trading_days, w.plates, w.gross, w.net, w.variable_expense]),
    ["TOTAL", s.trading_days, s.plates, s.gross, s.net, s.variable_expense],
    [22, 14, 10, 14, 14, 16], { 4: NUM, 5: NUM2, 6: NUM });
  tableSheet(wb, "Item Summary", "ITEM-WISE SALES", r,
    ["Category", "Item", "Rate (₹)", "Day Qty", "Night Qty", "Total Qty", "Total Sales (₹)", "% of Sales"],
    r.items.map((i) => [i.category, i.name, i.rate, i.day_qty, i.night_qty, i.qty, i.sales, i.pct]),
    ["", "TOTAL", "", r.items.reduce((a, i) => a + i.day_qty, 0), r.items.reduce((a, i) => a + i.night_qty, 0), s.plates, s.gross, 1],
    [14, 40, 10, 10, 10, 10, 16, 12], { 3: NUM, 7: NUM, 8: "0.0%" });
  tableSheet(wb, "Expense Summary", "EXPENSE REPORT", r,
    ["Type", "Expense Head", "Amount (₹)", "% of Expense"],
    r.expenses.map((e) => [e.kind === "fixed" ? "Fixed (pro-rated)" : e.category, e.name, e.amount, s.total_expense ? e.amount / s.total_expense : 0]),
    ["", "TOTAL EXPENSE", s.total_expense, 1], [18, 32, 16, 14], { 3: NUM2, 4: "0.0%" });
  const pb = r.paid_by || [], pName = (p) => (p.name === "Unassigned" ? "Not assigned" : `${p.name} paid (₹)`);
  const pTot = pb.reduce((a, p) => a + p.amount, 0);
  const pFmt = Object.fromEntries(pb.map((_, i) => [i + 3, NUM]));
  tableSheet(wb, "Paid By (Date-wise)", "EXPENSES PAID BY — DATE-WISE", r,
    ["Date", "Day", ...pb.map(pName), "Total (₹)"],
    r.days.map((d) => [fmtDate(d.date, { day: "2-digit", month: "short", year: "numeric" }), fmtDate(d.date, { weekday: "short" }), ...pb.map((p) => p.by_date[d.date] || 0), pb.reduce((a, p) => a + (p.by_date[d.date] || 0), 0)]),
    ["TOTAL", "", ...pb.map((p) => p.amount), pTot], [14, 8, ...pb.map(() => 18), 14], { ...pFmt, [pb.length + 3]: NUM });
  const vh = r.expenses.filter((e) => e.kind === "variable" && e.amount);
  tableSheet(wb, "Paid By (Head-wise)", "EXPENSES PAID BY — HEAD-WISE", r,
    ["Category", "Expense Head", ...pb.map(pName), "Total (₹)"],
    vh.map((h) => [h.category, h.name, ...pb.map((p) => p.by_head[h.id] || 0), h.amount]),
    ["", "TOTAL", ...pb.map((p) => p.amount), pTot], [14, 32, ...pb.map(() => 18), 14], { ...pFmt, [pb.length + 3]: NUM });

  const buf = await wb.xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `Manjap_Tracker_${r.start}_to_${r.end}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
